import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import type { AwCtx } from '../../ctx';
import { baht, card, dmy, errBox, note, num, sectionHead, table, td, th } from '../../ui';
import { Dialog, Loader, actBtn } from '../../dialog';
import type { FaRecon, FaReconAccount, FaReconReason } from './shared';

// ── The register against the general ledger (batch 9 R4 — COA-H14), full screen, read-only ──────
// GET /fixed-assets/reconciliation (services/assetRecon.ts) does every sum; this window lays them out.
// Two kinds of explanation, and the difference between them is the point of the screen:
//   · REASONS are provable from the two sides alone (a charge still in a draft, a charge with no entry,
//     a disposal not posted), so the server subtracts them from the difference;
//   · NOTES are worth knowing and prove nothing (an asset with no acquisition entry of its own, an
//     opening accumulated figure from outside), so they are never subtracted.
// What is left is `unexplained` — the one number here that means something is actually wrong. Each
// asset number opens that asset's window (over this one, so closing it comes back here).

/** Each code the server sends, each key written in full so a grep finds it. */
const REASON_KEY: Record<string, string> = {
  depreciation_in_draft: 'aw_far_rec_r_depreciation_in_draft',
  depreciation_without_an_entry: 'aw_far_rec_r_depreciation_without_an_entry',
  disposal_not_posted: 'aw_far_rec_r_disposal_not_posted',
  no_acquisition_entry_of_its_own: 'aw_far_rec_n_no_acquisition_entry_of_its_own',
  opening_accum_from_outside: 'aw_far_rec_n_opening_accum_from_outside',
};
const bannerOk: React.CSSProperties = { ...card, padding: '11px 14px', background: '#E8F6EE', border: '1px solid #BBE5CB', color: '#166534', fontWeight: 700, fontSize: 13 };
const bannerBad: React.CSSProperties = { ...card, padding: '11px 14px', background: '#FCEBEB', border: '1px solid #F3B9B9', color: '#B42318', fontWeight: 700, fontSize: 13 };
const diffTone = (n: number): React.CSSProperties => (n ? { color: '#B42318', fontWeight: 700 } : {});

export function ReconWindow(p: { ctx: AwCtx; onClose: () => void; onOpenAsset: (assetNo: string) => void }) {
  const { ctx, onClose, onOpenAsset } = p;
  const { t, i18n } = useTranslation();
  const [r, setR] = useState<FaRecon | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.faReconciliation(ctx.clientId, ctx.periodId)
      .then((x: FaRecon) => { if (alive) { setR(x); setErr(''); } })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload]);

  const acctName = (a: FaReconAccount) => (i18n.language === 'th' ? a.nameTh || a.nameEn : a.nameEn || a.nameTh);

  const side = (key: 'cost' | 'accum', title: string) => {
    const s = r![key];
    return (
      <div style={{ ...card, padding: 0 }} data-fa-rec-side={key}>
        <div style={{ ...sectionHead, padding: '11px 14px 0' }}>{title}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_far_rec_col_account')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_rec_col_register')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_rec_col_ledger')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_rec_col_diff')}</th>
              </tr>
            </thead>
            <tbody>
              {s.accounts.length === 0 && <tr><td colSpan={4} style={{ ...td, color: 'var(--text-tertiary)' }}>{t('aw_far_rec_no_accounts')}</td></tr>}
              {s.accounts.map(a => (
                <tr key={a.accountId} data-fa-rec-account={a.accountId}>
                  <td style={td}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{a.code}</b> · {acctName(a)}</td>
                  <td style={num}>{baht(a.register)}</td>
                  <td style={num}>{baht(a.gl)}</td>
                  <td style={{ ...num, ...diffTone(a.diff) }} data-fa-rec-diff={a.diff}>{baht(a.diff)}</td>
                </tr>
              ))}
              <tr data-fa-rec-total={key}>
                <td style={{ ...td, fontWeight: 800 }}>{t('aw_far_rec_total')}</td>
                <td style={{ ...num, fontWeight: 800 }}>{baht(s.register)}</td>
                <td style={{ ...num, fontWeight: 800 }}>{baht(s.gl)}</td>
                <td style={{ ...num, fontWeight: 800, ...diffTone(s.diff) }}>{baht(s.diff)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const explanations = (list: FaReconReason[], title: string, kind: 'reason' | 'note') => (
    <div style={card} data-fa-rec-list={kind}>
      <div style={sectionHead}>{title}</div>
      {list.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('aw_far_rec_none')}</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map(x => (
            <div key={x.code} data-fa-rec-code={x.code} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>
                  {REASON_KEY[x.code] ? t(REASON_KEY[x.code], { n: x.assets }) : t('aw_far_rec_other', { n: x.assets })}
                </div>
                {x.assetNos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)' }}>{t('aw_far_rec_assets')}:</span>
                    {x.assetNos.map(no => (
                      <button key={no} type="button" data-fa-rec-asset={no} onClick={() => onOpenAsset(no)} title={t('aw_far_rec_open_asset', { no })}
                        style={{ ...actBtn, padding: '1px 7px', fontSize: 11.5, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums' }}>
                        {no}
                      </button>
                    ))}
                    {x.assets > x.assetNos.length && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('aw_far_rec_more', { n: x.assets - x.assetNos.length })}</span>}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} data-fa-rec-amount={x.amount}>{baht(x.amount)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog fullscreen title={t('aw_far_rec_title')} docNo={r ? t('aw_far_rec_asof', { date: dmy(r.asOf) }) : undefined} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1100 }} data-fa-recon="1">
        {err && <div style={errBox}>{err}</div>}
        {!r && !err && <Loader />}
        {r && (
          <>
            <div style={r.balanced ? bannerOk : bannerBad} data-fa-rec-balanced={r.balanced ? '1' : '0'}>
              <i className={r.balanced ? 'ti ti-circle-check' : 'ti ti-alert-triangle'} aria-hidden />{' '}
              {r.balanced ? t('aw_far_rec_balanced') : t('aw_far_rec_unbalanced', { amount: baht(r.unexplained) })}
            </div>
            <div style={note}>{t('aw_far_rec_explain')}</div>
            {side('cost', t('aw_far_rec_cost'))}
            {side('accum', t('aw_far_rec_accum'))}
            <div style={{ ...card, padding: 0 }} data-fa-rec-side="nbv">
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>{' '}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_rec_col_register')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_rec_col_ledger')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_rec_col_diff')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...td, fontWeight: 800 }}>{t('aw_far_rec_nbv')}</td>
                    <td style={{ ...num, fontWeight: 800 }}>{baht(r.nbv.register)}</td>
                    <td style={{ ...num, fontWeight: 800 }}>{baht(r.nbv.gl)}</td>
                    <td style={{ ...num, fontWeight: 800, ...diffTone(r.nbv.diff) }}>{baht(r.nbv.diff)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {explanations(r.reasons, t('aw_far_rec_reasons'), 'reason')}
            {explanations(r.notes, t('aw_far_rec_notes'), 'note')}
            <div style={{ ...(r.balanced ? bannerOk : bannerBad), display: 'flex', justifyContent: 'space-between', gap: 12 }} data-fa-rec-unexplained={r.unexplained}>
              <span>{t('aw_far_rec_unexplained')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(r.unexplained)}</span>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
